<template>
  <div class="modern-form">
    <div class="form-container">
      <!-- Medical Title -->
      <div class="section-header">
        <h2 class="section-title">Medical</h2>
      </div>

      <div v-if="this.patient" class="patient-header">
        {{ patient.lastName }}, {{ patient.firstName }} ({{ patient.birthDate }})
      </div>

      <div v-if="needsUpdate" class="instructions-text">
        Your health history is due for an update. Please review and update any changes below.
      </div>
      <div v-else class="instructions-text">
        Please indicate whether you have any problems or conditions in the areas below. (If marked "yes" conditions will appear below)
      </div>

      <!-- Medical Condition Questionnaire -->
      <div class="form-section">
        <div class="question-row">
          <h4 class="question-title">Heart / Circulation</h4>
          <div class="modern-toggle">
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondHeart === 0 }"
              @click="hasCondHeart = 0">
              No
            </button>
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondHeart === 1 }"
              @click="hasCondHeart = 1">
              Yes
            </button>
          </div>
        </div>
        <div v-if="hasCondHeart === 1" class="checkbox-grid">
          <v-checkbox 
            v-for="item in itemsHeart" 
            :key="item.key"
            v-model="item.selected"
            :label="item.label"
            class="checkbox-item">
          </v-checkbox>
        </div>
      </div>

      <div class="form-section">
        <div class="question-row">
          <h4 class="question-title">Blood Disorder / Cancer</h4>
          <div class="modern-toggle">
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondBDC === 0 }"
              @click="hasCondBDC = 0">
              No
            </button>
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondBDC === 1 }"
              @click="hasCondBDC = 1">
              Yes
            </button>
          </div>
        </div>
        <div v-if="hasCondBDC === 1" class="checkbox-grid">
          <v-checkbox 
            v-for="item in itemsBD" 
            :key="item.key"
            v-model="item.selected"
            :label="item.label"
            class="checkbox-item">
          </v-checkbox>
        </div>
      </div>

      <div class="form-section">
        <div class="question-row">
          <h4 class="question-title">Liver / Kidney / Intestine</h4>
          <div class="modern-toggle">
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondLKI === 0 }"
              @click="hasCondLKI = 0">
              No
            </button>
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondLKI === 1 }"
              @click="hasCondLKI = 1">
              Yes
            </button>
          </div>
        </div>
        <div v-if="hasCondLKI === 1" class="checkbox-grid">
          <v-checkbox 
            v-for="item in itemsLKI" 
            :key="item.key"
            v-model="item.selected"
            :label="item.label"
            class="checkbox-item">
          </v-checkbox>
        </div>
      </div>

      <div class="form-section">
        <div class="question-row">
          <h4 class="question-title">Bone or joint Conditions</h4>
          <div class="modern-toggle">
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondBJC === 0 }"
              @click="hasCondBJC = 0">
              No
            </button>
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondBJC === 1 }"
              @click="hasCondBJC = 1">
              Yes
            </button>
          </div>
        </div>
        <div v-if="hasCondBJC === 1" class="checkbox-grid">
          <v-checkbox 
            v-for="item in itemsBJC" 
            :key="item.key"
            v-model="item.selected"
            :label="item.label"
            class="checkbox-item">
          </v-checkbox>
        </div>
      </div>

      <div class="form-section">
        <div class="question-row">
          <h4 class="question-title">Lung / Endocrine / Tobacco</h4>
          <div class="modern-toggle">
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondLET === 0 }"
              @click="hasCondLET = 0">
              No
            </button>
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondLET === 1 }"
              @click="hasCondLET = 1">
              Yes
            </button>
          </div>
        </div>
        <div v-if="hasCondLET === 1" class="checkbox-grid">
          <v-checkbox 
            v-for="item in itemsLET" 
            :key="item.key"
            v-model="item.selected"
            :label="item.label"
            class="checkbox-item">
          </v-checkbox>
        </div>
      </div>

      <div class="form-section">
        <div class="question-row">
          <h4 class="question-title">For Women</h4>
          <div class="modern-toggle">
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondWomen === 0 }"
              @click="hasCondWomen = 0">
              No
            </button>
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondWomen === 1 }"
              @click="hasCondWomen = 1">
              Yes
            </button>
          </div>
        </div>
        <div v-if="hasCondWomen === 1" class="checkbox-grid">
          <v-checkbox 
            v-for="item in itemsWomen" 
            :key="item.key"
            v-model="item.selected"
            :label="item.label"
            class="checkbox-item">
          </v-checkbox>
        </div>
      </div>

      <div class="form-section">
        <div class="question-row">
          <h4 class="question-title">Other Conditions</h4>
          <div class="modern-toggle">
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondOther === 0 }"
              @click="hasCondOther = 0">
              No
            </button>
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCondOther === 1 }"
              @click="hasCondOther = 1">
              Yes
            </button>
          </div>
        </div>
        <div v-if="hasCondOther === 1" class="form-row">
          <div class="form-group">
            <v-text-field 
              v-model="otherConditions" 
              label="Please specify"
              class="line-input custom-filled"
              dense
              hide-details="auto">
            </v-text-field>
          </div>
        </div>
      </div>

      <div class="form-section">
        <div class="question-row">
          <h4 class="question-title">Allergies</h4>
          <div class="modern-toggle">
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasAllergies === 0 }"
              @click="hasAllergies = 0">
              No
            </button>
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasAllergies === 1 }"
              @click="hasAllergies = 1">
              Yes
            </button>
          </div>
        </div>
        <div v-if="hasAllergies === 1" class="checkbox-grid">
          <v-checkbox 
            v-for="item in itemsAllergies" 
            :key="item.key"
            v-model="item.selected"
            :label="item.label"
            class="checkbox-item">
          </v-checkbox>
        </div>
      </div>

      <div class="form-section">
        <div class="question-row">
          <h4 class="question-title">Are you taking medications?</h4>
          <div class="modern-toggle">
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCurrMedications === 0 }"
              @click="hasCurrMedications = 0">
              No
            </button>
            <button 
              type="button"
              class="toggle-option"
              :class="{ active: hasCurrMedications === 1 }"
              @click="hasCurrMedications = 1">
              Yes
            </button>
          </div>
        </div>
        <div v-if="hasCurrMedications === 1" class="form-row">
          <div class="form-group">
            <v-combobox
              v-model="medications"
              label="Specify Medications (press tab to separate)"
              multiple
              chips
              class="line-input custom-filled"
              dense
              hide-details="auto" />
          </div>
        </div>
      </div>

      <Disclaimer ref="disclaimer" />
    </div>
  </div>

</template>

<script>
import { jsPDF } from 'jspdf';
import DataService from '../../../_services/DataService';
import emitEvent from '../../mixins/emitEvent';
import Disclaimer from './Disclaimer.vue';
import dateUtils from '@/../../backend/app/utils/dateUtils';

export default {
  name: 'MedicalForm',
  props: ['patient'],
  mixins: [emitEvent],
  components: {
    Disclaimer,
  },
  data() {
    return {
      hasCondHeart: 0,
      hasCondBDC: 0,
      hasCondLKI: 0,
      hasCondBJC: 0,
      hasCondLET: 0,
      hasCondWomen: 0,
      hasCondOther: 0,
      hasAllergies: 0,
      hasCurrMedications: 0,
      itemsHeart: [],
      itemsBD: [],
      itemsLKI: [],
      itemsBJC: [],
      itemsLET: [],
      itemsWomen: [],
      otherConditions: '',
      itemsAllergies: [],
      medications: [],
      previousMedications: [],
      needsUpdate: false,
      form: {
        id: 3,
      },
    };
  },
  watch: {
    async patient() {
      await this.getData();
      await this.checkUpdateNeeded();
    },
  },
  methods: {
    async checkUpdateNeeded() {
      this.needsUpdate = false;
      if (!this.patient || !this.patient.id) return;

      const latestMedicalFormResponse = await DataService.getLastPatientMedicalForm(this.patient.id);
      if (latestMedicalFormResponse.status === 200) {
        const latestMedicalFormDate = new Date(latestMedicalFormResponse.data.DateCreated);
        const now = new Date();
        const diffInDays = dateUtils.getDayDifference(latestMedicalFormDate, now);

        const client = await DataService.getClientDetails();
        const interval = client?.intakeFormUpdateInterval ?? 365;

        if (diffInDays >= interval) {
          this.needsUpdate = true;
        }
      }
      this.emitEvent('setNeedsUpdate', this.needsUpdate);
    },
    addMedicationField() {
      this.medicationFields.push({ 
        value: '',
      });
    },
    removeMedicationField(index) {
      this.medicationFields.splice(index, 1);
    },
    async validate() {
      return this.$refs.disclaimer.validate();
    },
    async sendForm(base64PDF) {
      this.form = {
        'id': 3,
        'heartIds': this.getSelectedIds(this.itemsHeart),
        'BDIds': this.getSelectedIds(this.itemsBD),
        'LKIIds': this.getSelectedIds(this.itemsLKI),
        'BJCIds': this.getSelectedIds(this.itemsBJC),
        'LETIds': this.getSelectedIds(this.itemsLET),
        'WomenIds': this.getSelectedIds(this.itemsWomen),
        'otherConditions': this.otherConditions,
        'allergyIds': this.getSelectedIds(this.itemsAllergies),
        'medications': this.medications,
        'base64PDF': base64PDF,
        'signature': this.$refs.disclaimer.$refs.signaturePad._data.signatureData,
        'removedHeartIds': this.getUnselectedIds(this.itemsHeart),
        'removedBDIds': this.getUnselectedIds(this.itemsBD),
        'removedLKIIds': this.getUnselectedIds(this.itemsLKI),
        'removedBJCIds': this.getUnselectedIds(this.itemsBJC),
        'removedLETIds': this.getUnselectedIds(this.itemsLET),
        'removedWomenIds': this.getUnselectedIds(this.itemsWomen),
        'removedAllergyIds': this.getUnselectedIds(this.itemsAllergies),
        'removedMedications': this.previousMedications.filter((med) => !this.medications.includes(med)),
      };

      const response = await (this.needsUpdate
        ? DataService.updatePatRegistration(this.form, this.patient.id)
        : DataService.postNewPatRegistration(this.form, this.patient.id));

      if (response.status === 200) {
        this.emitEvent('setNeedsUpdate', this.needsUpdate);
        this.emitEvent('goToNextForm');
        this.$root.$emit('raise_alert', { color: 'success', text: 'Your entry has been recorded.' });
      } else {
        this.emitEvent('submitFailure');
        this.$root.$emit('raise_alert', { color: 'error', text: 'We were unable to save your medical info, please try again or contact our team if the issue persists' });
      }
    },
    getSelectedIds(array) {
      return array.filter((item) => item.selected).map((item) => item.id);
    },
    getUnselectedIds(array) {
      return array.filter((item) => !item.selected).map((item) => item.id);
    },
    async getData() {
      const [
        clientDiseaseCategoriesResponse,
        diseaseDefinitionsResponse,
        allergyDefinitionsResponse,
        patientDiseasesResponse,
        patientAllergiesResponse,
        patientMedicationsResponse,
      ] = await Promise.all([
        DataService.getClientDiseaseCategories(),
        DataService.getDiseaseDefinitions(),
        DataService.getAllergyDefinitions(),
        DataService.getPatientDiseases(this.patient.id),
        DataService.getPatientAllergies(this.patient.id),
        DataService.getPatientMedications(this.patient.id),
      ]);

      if (
        diseaseDefinitionsResponse.status !== 200 ||
        allergyDefinitionsResponse.status !== 200 ||
        clientDiseaseCategoriesResponse.status !== 200
      ) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: 'An error occurred while loading the clinic configuration for this form.',
        });

        return;
      }

      let previouslySelectedDiseases = [];
      if (patientDiseasesResponse.status === 200) {
        previouslySelectedDiseases = patientDiseasesResponse.data.map(obj => obj.diseaseId);
      }

      let previouslySelectedAllergies = [];
      if (patientAllergiesResponse.status === 200) {
        previouslySelectedAllergies = patientAllergiesResponse.data.map(obj => obj.allergyId);
      }

      let previouslySelectedMedications = [];
      if (patientMedicationsResponse.status === 200) {
        const hasDateStopped = (value) => {
          if (value === null || value === undefined || value === '0001-01-01T00:00:00') {
            return false;
          }

          return String(value).trim().length > 0;
        };

        previouslySelectedMedications = patientMedicationsResponse.data
          .filter((obj) => !hasDateStopped(obj.dateStopped))
          .map((obj) => obj.description);
      }

      const heartAndCirculation = clientDiseaseCategoriesResponse.data.find((cat) => cat.category === 'Heart / Circulation');
      const bloodAndCancer = clientDiseaseCategoriesResponse.data.find((cat) => cat.category === 'Blood Disorder / Cancer');
      const liverKidneyIntestine = clientDiseaseCategoriesResponse.data.find((cat) => cat.category === 'Liver / Kidney / Intestine');
      const boneAndJoint = clientDiseaseCategoriesResponse.data.find((cat) => cat.category === 'Bone / Joint Conditions');
      const lungEndocrineTabacco = clientDiseaseCategoriesResponse.data.find((cat) => cat.category === 'Lung / Endocrine / Tobacco');
      const women = clientDiseaseCategoriesResponse.data.find((cat) => cat.category === 'Women');

      const heartIds = heartAndCirculation?.openDentalDiseaseIds ?? [];
      const BDIds = bloodAndCancer?.openDentalDiseaseIds ?? [];
      const LKIIds = liverKidneyIntestine?.openDentalDiseaseIds ?? [];
      const BJCIds = boneAndJoint?.openDentalDiseaseIds ?? [];
      const LETIds = lungEndocrineTabacco?.openDentalDiseaseIds ?? [];
      const womenIds = women?.openDentalDiseaseIds ?? [];

      const selectedDiseaseSet = new Set(previouslySelectedDiseases.map((v) => `${v}`));
      const diseases = diseaseDefinitionsResponse.data.map((item) => ({
        id: item.DiseaseDefNum,
        label: item.DiseaseName,
        selected: selectedDiseaseSet.has(`${item.DiseaseDefNum}`),
      }));

      // place items in correct categories and toggle UI if includes previously selected
      this.itemsHeart = diseases.filter((item) => heartIds.includes(item.id));
      this.hasCondHeart = this.itemsHeart.some((item) => item.selected !== false) ? 1 : 0;

      this.itemsBD = diseases.filter((item) => BDIds.includes(item.id));
      this.hasCondBDC = this.itemsBD.some((item) => item.selected !== false) ? 1 : 0;

      this.itemsLKI = diseases.filter((item) => LKIIds.includes(item.id));
      this.hasCondLKI = this.itemsLKI.some((item) => item.selected !== false) ? 1 : 0;

      this.itemsBJC = diseases.filter((item) => BJCIds.includes(item.id));
      this.hasCondBJC = this.itemsBJC.some((item) => item.selected !== false) ? 1 : 0;

      this.itemsLET = diseases.filter((item) => LETIds.includes(item.id));
      this.hasCondLET = this.itemsLET.some((item) => item.selected !== false) ? 1 : 0;

      this.itemsWomen = diseases.filter((item) => womenIds.includes(item.id));
      this.hasCondWomen = this.itemsWomen.some((item) => item.selected !== false) ? 1 : 0;

      const selectedAllergySet = new Set(previouslySelectedAllergies.map((v) => `${v}`));
      this.itemsAllergies = allergyDefinitionsResponse.data.map((item) => ({
        id: item.AllergyDefNum,
        label: item.Description,
        selected: selectedAllergySet.has(`${item.AllergyDefNum}`),
      }));

      this.hasAllergies = previouslySelectedAllergies.length > 0 ? 1 : 0;

      this.medications = previouslySelectedMedications;
      this.previousMedications = [...previouslySelectedMedications];
      this.hasCurrMedications = previouslySelectedMedications.length > 0 ? 1 : 0;
    },
    async generatePDF() {
      let y = 20;
      const doc = new jsPDF();
      doc.setFontSize(10).text(10, y, 'Medical');

      y = this.generateLinesForSection(doc, y, 'Heart / Circulation Conditions', this.hasCondHeart, this.itemsHeart);
      y = this.generateLinesForSection(doc, y, 'Blood Disorder / Cancer', this.hasCondBDC, this.itemsBD);
      y = this.generateLinesForSection(doc, y, 'Liver / Kidney / Intestine', this.hasCondLKI, this.itemsLKI);
      y = this.generateLinesForSection(doc, y, 'Bone or joint Conditions', this.hasCondBJC, this.itemsBJC);
      y = this.generateLinesForSection(doc, y, 'Lung / Endocrine / Tobacco', this.hasCondLET, this.itemsLET);
      y = this.generateLinesForSection(doc, y, 'Women', this.hasCondWomen, this.itemsWomen);
      y = this.generateLinesForSection(doc, y, 'Other Conditions', this.hasCondOther, this.otherConditions);
      y = this.generateLinesForSection(doc, y, 'Allergies', this.hasAllergies, this.itemsAllergies);
      y = this.generateLinesForSection(doc, y, 'Current Medications', this.hasCurrMedications, this.medications, (item) => item);

      doc.addPage();
      y = 20;

      doc.text(10, y+=5, `Relationship to Patient: ${this.$refs.disclaimer.relationshipToPatient}`);

      if (this.$refs.disclaimer.relationshipToPatient !== 'Self') {
        doc.text(10, y+=5, `Name if not Patient: ${this.$refs.disclaimer.nameIfNotPatient}`);
      }

      doc.text(10, y+=5, `Date Signed: ${new Date().toISOString()}`);

      doc.addImage(this.$refs.disclaimer.$refs.signaturePad._data.signatureData, 'JPEG', 10, y+=10, 100, 70, undefined, 'FAST');

      await this.sendForm(doc.output('datauristring').split(',')[1], this.patient.id);
    },
    generateLinesForSection(doc, yStart, sectionName, sectionWasChecked, sectionItems, customTextFn) {
      let updatedY = yStart;

      if (updatedY > 285) {
        doc.addPage();
        updatedY = 20;
      }

      doc.setFont(undefined, 'bold').text(10, updatedY += 5, sectionName).setFont(undefined, 'normal');

      if (sectionWasChecked) {
        for (const item of sectionItems) {
          if (updatedY > 285) {
            doc.addPage();
            updatedY = 20;
          }
          
          doc.text(10, updatedY += 5, customTextFn ? customTextFn(item) : `- ${item.label}: ${item.selected ? 'Yes' : 'No'}`);
        }
      } else {
        doc.text(10, updatedY += 5, '- Patient indicated no conditions in this section');
      }

      return updatedY;
    },
  },
  async mounted() {
    await this.getData();
    await this.checkUpdateNeeded();
  },
};
</script>
<style lang="scss" scoped>
@import '@/scss/variables.scss';

// Form-specific color variables
$patient-header-bg-start: #133b54;
$patient-header-bg-end: #1a365d;
$patient-header-text: white;
$question-row-bg: #f8fafc;
$question-row-border: #e2e8f0;
$question-title-color: #374151;
$instructions-bg: #eff6ff;
$instructions-border: #bfdbfe;
$instructions-text: #1e40af;
$checkbox-grid-bg: #f8fafc;
$checkbox-grid-border: #e2e8f0;

.patient-header {
  background: linear-gradient(180deg, $patient-header-bg-start 0%, $patient-header-bg-end 100%);
  color: $patient-header-text;
  padding: 16px;
  border-radius: 8px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 24px;
}

.form-section {
  margin-bottom: 32px;
}

.instructions-text {
  background: $instructions-bg;
  border: 1px solid $instructions-border;
  border-radius: 6px;
  padding: 12px 16px;
  color: $instructions-text;
  font-size: 14px;
  text-align: center;
  margin-bottom: 24px;
  font-weight: 500;
}

.question-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: $question-row-bg;
  border: 1px solid $question-row-border;
  border-radius: 8px;
  margin-bottom: 16px;
  
  @media (max-width: $breakpoint-mobile) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

.question-title {
  font-size: 16px;
  font-weight: 600;
  color: $question-title-color;
  margin: 0;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 8px;
  padding: 16px;
  background: $checkbox-grid-bg;
  border: 1px solid $checkbox-grid-border;
  border-top: none;
  border-radius: 0 0 8px 8px;
  
  .checkbox-item {
    margin: 0 !important;
    
    ::v-deep .v-input--selection-controls__input {
      margin-right: 8px !important;
    }
  }
}
</style>
